import React, { useState, useEffect, useCallback } from "react";
import {
  CircularProgress,
  Container,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Typography,
  Button,
  Tooltip,
  Chip,
  Stack,
  Link,
  IconButton,
} from "@mui/material";

import { backend, API_URL } from "../../../config";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import UploadFile from "../../Inventory/components/UploadFile";

import readXlsxFile from "read-excel-file";

export const ValidateStock = (props) => {
  const [errorInfo, setErrorInfo] = useState({ error: false, message: "" });
  const [fileData, setFileData] = useState([]);
  const [fileProcessing, setFileProcessing] = useState(false);
  const [orderItems, setOrderItems] = useState([]);
  const [comparisonResults, setComparisonResults] = useState([]);

  const handleChangeData = async (data, file) => {
    setFileProcessing(true);
    let dataImported = [];

    switch (file.type) {
      case "text/csv":
        data
          .map((item) => item.data)
          .forEach((item) => {
            if (item.length > 1) {
              dataImported.push(item);
            }
          });

        break;
      case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        await readXlsxFile(file).then((data) => {
          data.forEach((item) => {
            if (item.length > 1) {
              dataImported.push(item);
            }
          });
        });

        break;
      default:
        setErrorInfo({
          error: true,
          message: "Unsupported file type. Please usload a .csv or .xlsx file.",
        });
    }

    if (!dataImported.length) {
      setErrorInfo({
        error: true,
        message:
          "The imported file couldn't be processed (it didn't generate any data to be imported).",
      });
      return;
    } else {
      const headersIndex = dataImported.findIndex((item) =>
        String(item).toLowerCase().includes("quantity on hand"),
      );

      if (headersIndex === -1) {
        setErrorInfo({
          error: true,
          message:
            "The imported file couldn't be processed (couldn't find the headers from the imported data: quantity on hand).",
        });
        return;
      }

      const headersRow = dataImported[headersIndex].map((item) =>
        String(item || "")
          .trim()
          .toLowerCase(),
      );

      const headersMap = {
        item_code: headersRow.indexOf("item"),
        quantity_on_hand: headersRow.indexOf("quantity on hand"),
        uom: headersRow.indexOf("u/m"),
      };

      const rowsMap = dataImported.slice(headersIndex + 1).map((row) => ({
        item_code: String(row[headersMap.item_code] || "").trim(),
        quantity_on_hand: String(row[headersMap.quantity_on_hand] || "").trim(),
        uom: String(row[headersMap.uom] || "").trim(),
      }));

      const query = new URLSearchParams(props?.location?.search);

      const dateStr = query.get("date");

      if (!dateStr) {
        setErrorInfo({
          error: true,
          message: "Couldn't find a proper date to get orders to validate",
        });
        return;
      }

      await backend
        .get(
          `${API_URL}orders?no_page=1&start_date=${dateStr}&end_date=${dateStr}`,
        )
        .then(async ({ data }) => {
          if (!data?.length) {
            setErrorInfo({
              error: true,
              message: "Couldn't find any orders for the selected date",
            });
            return;
          } else {
            const orderIds = data.map((item) => item.id);
            const orderIdStr = orderIds.join("&order=");

            await backend
              .get(
                `${API_URL}orderitems?no_page=1&product_detail=True&order=${orderIdStr}`,
              )
              .then(({ data }) => {
                let addedUPCs = [];
                let orderItemsBulk = [];

                data
                  .filter((item) =>
                    [3, 4].includes(item?.product_data?.warehouse?.id),
                  )
                  .forEach((item) => {
                    const newIndicator = `${item.product}_${
                      item.bin_override !== ""
                        ? item.bin_override
                        : item.product_data.bin
                    }`;

                    if (addedUPCs.includes(newIndicator)) return;

                    const dupItemsArray = data.filter(
                      (oiItem) =>
                        oiItem.product === item.product &&
                        oiItem.bin_override === item.bin_override,
                    );

                    let unit_quantitySum = 0,
                      case_quantitySum = 0,
                      units_orderedSum = 0,
                      cases_orderedSum = 0;

                    dupItemsArray.forEach((dItem) => {
                      unit_quantitySum += dItem.unit_quantity || 0;
                      case_quantitySum += dItem.case_quantity || 0;
                      units_orderedSum += dItem.units_ordered || 0;
                      cases_orderedSum += dItem.cases_ordered || 0;
                    });

                    orderItemsBulk.push({
                      item_code: item.product_data.item_code.item_code,
                      product: item.product,
                      name: item.product_data.name,
                      par_stock: item.product_data.par_stock,
                      reorder_quantity: item.product_data.reorder_quantity,
                      weight_type: item.product_data.weight_type,
                      average_unit_weight:
                        item.product_data.average_unit_weight,
                      average_case_weight:
                        item.product_data.average_case_weight,
                      unit_quantity: unit_quantitySum,
                      case_quantity: case_quantitySum,
                      units_ordered: units_orderedSum,
                      cases_ordered: cases_orderedSum,
                    });

                    addedUPCs.push(newIndicator);
                  });
                setOrderItems(orderItemsBulk);
              });
          }
        });

      setFileData(rowsMap);
    }
  };

  const errorFallback = () => {
    setErrorInfo({ error: false, message: "" });
    setFileData([]);
    setFileProcessing(false);
    setComparisonResults([]);
  };

  const getCellBackground = (item, cell = null) => {
    if (cell === null && item.weight_type !== "") return "rgba(0, 0, 0, 0.04)";

    switch (cell) {
      case "qb_on_hand":
        return !item.exist
          ? "lightyellow"
          : item.weight_type !== ""
            ? "rgba(0, 0, 0, 0.04)"
            : "inherit";
      case "qb_uom":
        return !item.exist
          ? "lightyellow"
          : item.weight_type !== ""
            ? "rgba(0, 0, 0, 0.04)"
            : "inherit";
      case "availability":
        return item.exist && item.availability ? "lightgreen" : "lightyellow";
      case "par_stock":
        if (item.par_stock === null) return "lightblue";
        return item.difference_on_hand >= item.par_stock
          ? "lightgreen"
          : "lightyellow";
      case "reorder_quantity":
        if (item.par_stock === null) return "inherit";
        return item.difference_on_hand >= item.par_stock
          ? "inherit"
          : "lightyellow";
      default:
        return "inherit";
    }
  };

  const compareFileData = useCallback(() => {
    const comparisonData = orderItems.map((item) => {
      const fileItem = fileData.find(
        (fdItem) => String(fdItem.item_code) === String(item.item_code),
      );

      const qbOnHand = fileItem
        ? parseFloat(fileItem.quantity_on_hand || 0)
        : 0;

      const qbUOM = fileItem ? String(fileItem.uom || "") : "";

      const unitConversion =
        item.weight_type === ""
          ? item.unit_quantity
          : (item.average_unit_weight || 1) * item.unit_quantity;
      const caseConversion =
        item.weight_type === ""
          ? item.case_quantity
          : (item.average_case_weight || 1) * item.case_quantity;

      const differenceOnHand = qbOnHand - unitConversion - caseConversion;

      const availability = differenceOnHand >= 0;

      const amountToRestock =
        differenceOnHand <= 0
          ? (item.par_stock || 0) + Math.abs(differenceOnHand)
          : (item.par_stock || 0) - differenceOnHand;

      return {
        upc: item.product,
        item_code: item.item_code,
        description: item.name,
        unit_quantity: item.unit_quantity,
        units_ordered: item.units_ordered,
        case_quantity: item.case_quantity,
        cases_ordered: item.cases_ordered,
        qb_on_hand: qbOnHand,
        qb_uom: qbUOM,
        exist: Boolean(fileItem),
        availability: availability,
        difference_on_hand: differenceOnHand,
        amount_to_restock: amountToRestock.toFixed(2),
        unit_conversion: unitConversion,
        case_conversion: caseConversion,
        weight_type: item.weight_type,
        par_stock: item.par_stock,
        reorder_quantity: item.reorder_quantity,
      };
    });

    setComparisonResults(comparisonData);
    setFileProcessing(false);
  }, [fileData, orderItems]);

  useEffect(() => {
    if (fileData.length && orderItems.length && !errorInfo.error) {
      compareFileData();
    }
  }, [fileData, orderItems, errorInfo, compareFileData]);

  return (
    <Container>
      {!fileData.length && !fileProcessing ? (
        <UploadFile
          {...props}
          errorInfo={errorInfo}
          setErrorInfo={setErrorInfo}
          handleChangeData={handleChangeData}
        />
      ) : fileProcessing ? (
        <div align="center">
          <br />
          <CircularProgress />
          <div>Processing file data</div>
          {errorInfo.error ? (
            <>
              <br />
              <br />
              <div>
                <Typography component="h4" color="error">
                  {errorInfo.message}
                </Typography>
              </div>
              <div align="right">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => errorFallback()}
                >
                  Continue
                </Button>
              </div>
            </>
          ) : (
            <></>
          )}
        </div>
      ) : (
        <></>
      )}
      {!errorInfo.error && comparisonResults.length ? (
        <>
          <Typography component="h6" variant="h6" align="center">
            {`Quickbooks vs Software stock validation`}
          </Typography>
          <TableContainer style={{ maxHeight: "80vh" }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell size="small">Item Code</TableCell>
                  <TableCell size="small">Description</TableCell>
                  <TableCell size="small">Unit Ordered</TableCell>
                  <TableCell size="small">Case Ordered</TableCell>
                  <TableCell size="small">Par Stock</TableCell>
                  <TableCell size="small">QB On hand</TableCell>
                  <TableCell size="small">QB U/M</TableCell>
                  <TableCell size="small">Remaining Stock</TableCell>
                  <TableCell size="small">Availability</TableCell>
                  <TableCell size="small">Low Stock</TableCell>
                  <TableCell size="small">Reorder Quantity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparisonResults.map((item, index) => (
                  <TableRow key={`row-${index}`} hover tabIndex={-1}>
                    <TableCell
                      size="small"
                      style={{
                        background: getCellBackground(item),
                      }}
                    >
                      {item.item_code}
                    </TableCell>
                    <TableCell
                      size="small"
                      style={{
                        background: getCellBackground(item),
                      }}
                    >
                      {item.description}
                    </TableCell>
                    <TableCell
                      size="small"
                      style={{
                        background: getCellBackground(item),
                      }}
                    >
                      {item.units_ordered}
                    </TableCell>
                    <TableCell
                      size="small"
                      style={{
                        background: getCellBackground(item),
                      }}
                    >
                      {item.cases_ordered}
                    </TableCell>
                    <TableCell
                      size="small"
                      style={{
                        background: getCellBackground(item),
                        textAlign: "center",
                      }}
                    >
                      {item.par_stock ? parseFloat(item.par_stock).toFixed(2) : ""}
                    </TableCell>
                    <TableCell
                      size="small"
                      style={{
                        background: getCellBackground(item, "qb_on_hand"),
                        textAlign: "center",
                      }}
                    >
                      {parseFloat(item.qb_on_hand).toFixed(2)}
                    </TableCell>
                    <TableCell
                      size="small"
                      style={{
                        background: getCellBackground(item, "qb_uom"),
                        textAlign: "center",
                      }}
                    >
                      {item.exist ? item.qb_uom : <CloseIcon />}
                    </TableCell>
                    <TableCell
                      size="small"
                      style={{
                        background: getCellBackground(item, "availability"),
                        textAlign: "center",
                      }}
                    >
                      {!item.exist || !item.availability ? (
                        <Tooltip
                          title={`${!item.exist ? "Item not found in software" : `Not enough stock to fulfill the order. QB on hand needed: ${item.unit_conversion + item.case_conversion}.`}`}
                        >
                          <CloseIcon />
                        </Tooltip>
                      ) : (
                        <Tooltip
                          title={`QB on hand needed: ${item.unit_conversion + item.case_conversion}. In stock after fulfilling the order: ${parseFloat(item.difference_on_hand).toFixed(2)}.`}
                        >
                          <>{parseFloat(item.difference_on_hand).toFixed(2)}</>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell
                      size="small"
                      style={{
                        background: getCellBackground(item, "availability"),
                        textAlign: "center",
                      }}
                    >
                      {!item.exist || !item.availability ? (
                        <Tooltip
                          title={`${!item.exist ? "Item not found in software" : `Not enough stock to fulfill the order. QB on hand needed: ${item.unit_conversion + item.case_conversion}.`}`}
                        >
                          <CloseIcon />
                        </Tooltip>
                      ) : (
                        <Tooltip
                          title={`QB on hand needed: ${item.unit_conversion + item.case_conversion}. In stock after fulfilling the order: ${parseFloat(item.difference_on_hand).toFixed(2)}.`}
                        >
                          <CheckIcon />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell
                      size="small"
                      style={{
                        background: getCellBackground(item, "par_stock"),
                        textAlign: "center",
                      }}
                    >
                      {item.par_stock === null ? (
                        <Link href={`/product/${item.upc}`} target="_blank">
                          <Tooltip title="No par stock set for this item | Edit product">
                            <IconButton aria-label="edit product">
                              <OpenInNewIcon />
                            </IconButton>
                          </Tooltip>
                        </Link>
                      ) : item.difference_on_hand >= item.par_stock ? (
                        <Tooltip
                          title={`Difference between QB on hand and ordered quantity is higher than par stock (${item.par_stock}).`}
                        >
                          <CheckIcon />
                        </Tooltip>
                      ) : (
                        <Tooltip
                          title={`Difference between QB on hand and ordered quantity is lower than par stock (${item.par_stock}). Difference: ${item.amount_to_restock}.`}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <CloseIcon color="error" />
                            <Chip label={`${item.amount_to_restock}`} />
                          </Stack>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell
                      size="small"
                      style={{
                        background: getCellBackground(item),
                        textAlign: "center",
                      }}
                    >
                      {item.reorder_quantity ? parseFloat(item.reorder_quantity).toFixed(2) : ""}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <></>
      )}
    </Container>
  );
};

export default ValidateStock;
